import { useUtilisateurService, Utilisateur } from '@europrocurement/l2d-domain';
import { GroupesDeTravailJsonldUtilisateurReadDomaineRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';

export type LiteUtilisateur = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    userIdentifier: string;
};

export const useUtilisateur = () => {
    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;

    const { utilisateurApi, groupesTravailApi } = useUtilisateurService();

    const getUserByEmail = (emailAddress: string): Promise<Utilisateur> =>
        new Promise((resolve, reject) => {
            utilisateurApi
                .apiUtilisateursGetCollection({ mail: emailAddress })
                .then((response) => {
                    const user = response.data['hydra:member'][0] ?? null;

                    if (user && user.mail === emailAddress) {
                        resolve(user as Utilisateur);

                        return;
                    }

                    reject();
                })
                .catch(() => {
                    reject();
                });
        });

    const fetchUserGroups = (
        userId: number,
    ): Promise<GroupesDeTravailJsonldUtilisateurReadDomaineRead[]> =>
        new Promise((resolve, reject) => {
            groupesTravailApi
                .apiGroupestravailGetCollection({ iduserReferent: userId.toString() })
                .then((response) => {
                    const groups = response.data['hydra:member'] ?? null;

                    resolve(groups);
                })
                .catch(() => {
                    reject();
                });
        });

    const getUserGroups = async (): Promise<GroupesDeTravailJsonldUtilisateurReadDomaineRead[]> => {
        const emailAddress = keycloakService.getEmail() ?? null;

        if (!emailAddress) {
            return new Promise((resolve, reject) => {
                reject();
            });
        }

        const user = await getUserByEmail(emailAddress);

        if (user?.id) {
            const groups = await fetchUserGroups(user.id);

            return groups;
        }

        return [];
    };

    const getUserId = async (): Promise<number> => {
        const emailAddress = keycloakService.getEmail() ?? null;

        if (!emailAddress) {
            return new Promise((resolve, reject) => {
                reject();
            });
        }

        const user = await getUserByEmail(emailAddress);

        return user?.id as number;
    };

    const getCurrentUser = (): Promise<LiteUtilisateur> =>
        new Promise((resolve, reject) => {
            utilisateurApi
                .apiUtilisateursGetCollection({ userIdentifier })
                .then((response) => {
                    const user = response.data['hydra:member'][0] ?? null;

                    if (user && user.userIdentifier === userIdentifier) {
                        resolve({
                            id: user.id,
                            firstName: user.prenom,
                            lastName: user.nom,
                            email: user.mail,
                            userIdentifier: user.userIdentifier,
                        } as LiteUtilisateur);

                        return;
                    }

                    reject();
                })
                .catch(() => {
                    reject();
                });
        });

    return { getUserId, getUserGroups, getUserByEmail, getCurrentUser };
};
